import type { ClientDevice, ClientDeviceInput, ClientDeviceWithDevice } from '@maru44/huntre-utils/src/models/clientDevice'
import type { Device } from '@maru44/huntre-utils/src/models/device'
import { getData } from '@maru44/huntre-utils/src/utils/data'
import { collection, doc, documentId, getDoc, getDocs, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore'
import { useCallback } from 'react'
import { DuplicateError, NotFoundError } from 'src/utils/error'
import { firestore } from 'src/utils/firebase'
import { filter, safeQuery } from 'src/utils/firestoreQuery'
import useSWR from '../useSWR'

export const useClientDevices = (clientId: string) => {
  const listClientDevicesWithDevice = useSWR(
    [`/clients/${clientId}/clientDevices`, clientId],
    async () => {
      const udCollectionRef = collection(firestore, `clients/${clientId}/clientDevices`)
      const snap = await getDocs(udCollectionRef)

      const data: ClientDeviceWithDevice[] = []
      const deviceIds: string[] = []
      const deviceById: { [key: string]: Device } = {}

      const clientDevices = snap.docs.map((v) => getData<ClientDevice>(v))

      clientDevices.forEach((v) => {
        deviceIds.push(v.deviceId)
      })

      for (const ids of sliceByNum(deviceIds, 10)) {
        const ref = safeQuery(collection(firestore, `devices`), filter(documentId(), 'in', ids))
        const devices = await getDocs(ref)
        devices.forEach((ss) => {
          const device = getData<Device>(ss)
          deviceById[device.id] = device
        })
      }

      clientDevices.forEach((v) => {
        data.push({
          clientDevice: v,
          device: deviceById[v.deviceId],
        })
      })

      return data
    },
    {
      disabled: !clientId,
    },
  )

  const createClientDevice = useCallback(
    async (input: ClientDeviceInput) => {
      const deviceDocRef = doc(firestore, `devices/${input.deviceId}`)
      const deviceDoc = await getDoc(deviceDocRef)

      if (!deviceDoc.exists()) {
        throw new NotFoundError('device', input.deviceId)
      }

      const clientDeviceDocRef = doc(firestore, `clients/${clientId}/clientDevices/${input.deviceId}`)
      const clientDeviceDoc = await getDoc(clientDeviceDocRef)
      if (clientDeviceDoc.exists()) {
        throw new DuplicateError('clientDevice', {
          key: 'deviceId',
          value: input.deviceId,
        })
      }
      const now = serverTimestamp()
      await setDoc(clientDeviceDocRef, {
        deviceId: input.deviceId,
        isTurnedOff: input.isTurnedOff === undefined ? false : input.isTurnedOff,
        createdAt: now,
        updatedAt: now,
      })
    },
    [clientId],
  )

  const updateIsTurnedOff = useCallback(
    async (id: string, isTurnedOff: boolean) => {
      const ref = doc(firestore, `clients/${clientId}/clientDevices/${id}`)
      await updateDoc(ref, { isTurnedOff, updatedAt: serverTimestamp() })
    },
    [clientId],
  )

  return { listClientDevicesWithDevice, createClientDevice, updateIsTurnedOff }
}

const sliceByNum = <T>(array: T[], num: number): T[][] => {
  if (num <= 0) return [[]]
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += num) {
    chunks.push(array.slice(i, i + num))
  }
  return chunks
}
